import { displaySize } from "../../constants.js";

/**
 * Seed a text layer's per-element animation from the doc's whole-banner
 * preset, so "Customize layout" preserves the motion the preview was
 * already showing.
 */
function seedAnimation(role, preset) {
  if (role !== "headline" && role !== "subline" && role !== "cta") return undefined;
  const delay = role === "headline" ? 0 : role === "subline" ? 0.5 : 1;
  switch (preset) {
    case "static":
      return undefined;
    case "slide_build":
      return {
        in: { effect: role === "cta" ? "zoom-in" : "slide-left", delay, duration: 0.7 },
      };
    case "cta_pulse_finale":
      return role === "cta"
        ? {
            in: { effect: "fade", delay: 0.9, duration: 0.6 },
            emphasis: { effect: "pulse", delay: 1.8, duration: 0.8, times: 2 },
          }
        : { in: { effect: "fade", delay: 0, duration: 0.7 } };
    case "fade_sequence":
    default:
      return { in: { effect: "fade", delay, duration: 0.8 } };
  }
}

function newId() {
  return crypto.randomUUID();
}

/** The box for a layer at a given size, or undefined when it has none there. */
export function layerBoxFor(layer, sizeId) {
  return layer.boxes[sizeId];
}

/** Gather a template slot's box across sizes (only where that slot exists). */
function boxesForSlot(doc, layout, key) {
  const boxes = {};
  for (const sizeId of doc.sizes) {
    const b = layout[sizeId]?.[key];
    if (b) boxes[sizeId] = { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  return boxes;
}

function textLayer(role, text, boxes, layout, doc, z, extra = {}) {
  const firstSize = Object.keys(boxes)[0];
  const slotKey = role === "custom" ? "headline" : role;
  const slot = firstSize ? layout[firstSize]?.[slotKey] : undefined;
  // Freezes each size's own template-tuned font size at the moment of
  // conversion, so switching to advanced mode causes zero visual change -
  // not just for whichever size happens to be "first", but for every one.
  // Takes priority over fontSize/fontSizeAuto in resolveFontSize; clears
  // per-size once that size's box is actually dragged (see setLayerBox),
  // since the point is protecting the untouched initial state, not
  // permanently pinning a value the user hasn't chosen to keep.
  const fontSizeBySize = {};
  for (const sizeId of Object.keys(boxes)) {
    const fs = layout[sizeId]?.[slotKey]?.fontSize;
    if (Number.isFinite(fs)) fontSizeBySize[sizeId] = fs;
  }
  return {
    id: newId(),
    type: "text",
    role,
    z,
    text,
    fontSize: slot?.fontSize,
    fontSizeBySize,
    fontWeight: slot?.fontWeight,
    align: slot?.align,
    color: doc.slots.textColor,
    animation: seedAnimation(role, doc.animationPreset),
    boxes,
    ...extra,
  };
}

/** Convert a template-mode banner into advanced layers. */
export function layersFromTemplate(doc, layout) {
  const baked = new Set(doc.slots.bakedText ?? []);
  const layers = [];
  let z = 0;

  // background / image layer (bottom of the stack)
  const imageBoxes = {};
  for (const sizeId of doc.sizes) {
    const L = layout[sizeId];
    if (!L) continue;
    if (L.imageAsBackground) {
      const size = displaySize(sizeId);
      if (size) imageBoxes[sizeId] = { x: 0, y: 0, w: size.w, h: size.h };
    } else if (L.image) {
      imageBoxes[sizeId] = { x: L.image.x, y: L.image.y, w: L.image.w, h: L.image.h };
    }
  }
  if (doc.slots.imageSource && Object.keys(imageBoxes).length > 0) {
    layers.push({
      id: newId(),
      type: "image",
      role: "background",
      z: z++,
      imageSource: doc.slots.imageSource,
      fit: "cover",
      boxes: imageBoxes,
    });
  }

  // logo
  const logoBoxes = boxesForSlot(doc, layout, "logo");
  if (doc.slots.logoRefId && Object.keys(logoBoxes).length > 0) {
    layers.push({
      id: newId(),
      type: "image",
      role: "logo",
      z: z++,
      imageSource: { kind: "reference", id: doc.slots.logoRefId },
      fit: "contain",
      boxes: logoBoxes,
    });
  }

  // headline
  if (!baked.has("headline")) {
    const boxes = boxesForSlot(doc, layout, "headline");
    if (Object.keys(boxes).length > 0) {
      layers.push(textLayer("headline", doc.slots.headline, boxes, layout, doc, z++));
    }
  }
  // subline
  if (!baked.has("subline") && doc.slots.subline) {
    const boxes = boxesForSlot(doc, layout, "subline");
    if (Object.keys(boxes).length > 0) {
      layers.push(textLayer("subline", doc.slots.subline, boxes, layout, doc, z++));
    }
  }
  // cta (carries a button background)
  if (!baked.has("cta")) {
    const boxes = boxesForSlot(doc, layout, "cta");
    if (Object.keys(boxes).length > 0) {
      layers.push(
        textLayer("cta", doc.slots.cta, boxes, layout, doc, z++, {
          color: "#ffffff",
          bg: doc.slots.ctaColor,
        })
      );
    }
  }

  return layers;
}